// @flow
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Divider, Typography} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import makeStyles from '@material-ui/core/styles/makeStyles';

import CustomModal from '../../../../elements/CustomModal/CustomModal';
import type {Order, Country} from '../../../../entities';
import type {CheckoutRequest} from '../../../manual-order/api';
import {colors} from '../../../../assets/styles/colors';
import {Controller, useForm} from 'react-hook-form';

import type {RootState} from '../../../index';
import type {Dispatch} from 'redux';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {actions as ManualOrderActions} from '../../../manual-order/duck';
import {useCallback} from 'react';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import TelInput from '../../../../elements/TelInput/TelInput';
import {REGEX} from '../../../../constants';
import {sanitizePhoneNumber} from '../../../../utils/phone';

export default connect(
  (state: RootState) => ({
    editOrderSuccess: state.manualOrder.editOrderSuccess,
    editOrderError: state.manualOrder.editOrderError,
    country: state.country.country,
  }),
  (dispatch: Dispatch<any>) =>
    bindActionCreators(
      {
        ...ManualOrderActions,
      },
      dispatch
    )
)(EditCustomerInfoModal);

type Props = {
  isSaving?: boolean,
  open: boolean,
  handleClose: () => void,
  order: Order,
  country: Country,
  editOrder: ({checkoutRequest: CheckoutRequest}) => void,
};
const useStyles = makeStyles(theme => ({
  root: {
    // padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 340,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  textField: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
    width: '100%',
  },
  button: {
    height: 40,
    fontSize: '1rem',
    alignSelf: 'stretch',
    marginBottom: theme.spacing(1),
  },
}));

function EditCustomerInfoModal(props: Props) {
  const {isSaving, open, handleClose, order, country, editOrder} = props;
  const classes = useStyles();

  const {register, handleSubmit, errors, control} = useForm();

  const [phone, setPhone] = useState('');

  const handleCloseModal = useCallback(() => {
    if (!isSaving) {
      handleClose();
    }
  }, [handleClose, isSaving]);

  const handleAmountReceivedSubmit = data => {
    const request = {
      orderData: {
        customer: {
          email: data.email,
          firstName: data.firstName,
          identificationNumber: data.idNumber || undefined,
          // identificationType: data.identificationType,
          lastName: data.lastName,
          phone: sanitizePhoneNumber(phone),
        },
      },
    };
    editOrder({checkoutRequest: {id: order.id, request}});
  };

  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        <Typography className={classes.title} align="center">
          Editar información de cliente
        </Typography>
        <Divider className={classes.divider} />
        <CustomTextField
          label="Nombres"
          name="firstName"
          className={classes.textField}
          defaultValue={order.customer.firstName}
          error={!!errors.firstName}
          helperText={errors.firstName ? errors.firstName.message : ''}
          inputRef={register({
            required: 'Campo obligatorio',
          })}
          type="text"
          disabled={isSaving}
        />
        <CustomTextField
          label="Apellidos"
          name="lastName"
          className={classes.textField}
          defaultValue={order.customer.lastName}
          error={!!errors.lastName}
          helperText={errors.lastName ? errors.lastName.message : ''}
          inputRef={register({
            required: 'Campo obligatorio',
          })}
          type="text"
          disabled={isSaving}
        />
        <CustomTextField
          label="Documento de identidad (opcional)"
          name="idNumber"
          className={classes.textField}
          defaultValue={order.customer.identificationNumber}
          error={!!errors.idNumber}
          helperText={errors.idNumber ? errors.idNumber.message : ''}
          inputRef={register({
            // required: 'Campo obligatorio',
            // pattern: {
            //   value: /^[0-9]{8}$/i,
            //   message: 'Formato de DNI inválido',
            // },
          })}
          type="text"
          disabled={isSaving}
        />
        <Controller
          control={control}
          name="phone"
          defaultValue={phone}
          render={({onChange, value}) => (
            <TelInput
              label="Número telefónico"
              fullWidth
              className={classes.textField}
              name="phone"
              defaultValue={order.customer.phone}
              country={country}
              onChange={v => setPhone(v)}
              // inputRef={register({
              //   required: 'Campo obligatorio',
              // })}
              error={!!errors.phone}
              helperText={errors.phone ? errors.phone.message : ''}
            />
          )}
        />
        <CustomTextField
          label="Correo electrónico"
          name="email"
          className={classes.textField}
          defaultValue={order.customer.email}
          error={!!errors.email}
          helperText={errors.email ? errors.email.message : ''}
          inputRef={register({
            required: 'Campo obligatorio',
            pattern: {
              value: REGEX.email,
              message: 'Formato de email inválido',
            },
          })}
          type="text"
          disabled={isSaving}
        />
        <CustomButton
          className={classes.button}
          variant="contained"
          onClick={handleSubmit(handleAmountReceivedSubmit)}
          disabled={isSaving}
        >
          Guardar cambios
        </CustomButton>
        <CustomButton
          className={classes.button}
          onClick={handleCloseModal}
          disabled={isSaving}
        >
          Cancelar
        </CustomButton>
      </div>
    </CustomModal>
  );
}

EditCustomerInfoModal.propTypes = {
  isSaving: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  country: PropTypes.object,
  editOrder: PropTypes.func,
};
